import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { sendEmail } from '../config/emailConfig';

// Styled Components
const ChatbotContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
`;

const ChatbotButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #0066cc;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: #0055aa;
  }
`;

const ChatWindow = styled.div`
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 350px;
  height: 500px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: ${props => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  background-color: #0066cc;
  color: white;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatTitle = styled.h3`
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 16px;
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Message = styled.div`
  margin-bottom: 10px;
  max-width: 80%;
  padding: 10px 15px;
  border-radius: 18px;
  white-space: pre-wrap;
  
  ${props => props.isBot 
    ? `
      align-self: flex-start;
      background-color: #f0f0f0;
      color: #333;
    ` 
    : `
      align-self: flex-end;
      background-color: #0066cc;
      color: white;
    `
  }
`;

const OptionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
`;

const OptionButton = styled.button`
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 15px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const ChatInput = styled.div`
  padding: 15px;
  border-top: 1px solid #eee;
  display: flex;
`;

const InputField = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 20px;
  outline: none;
  
  &:focus {
    border-color: #0066cc;
  }
`;

const SendButton = styled.button`
  margin-left: 10px;
  background-color: #0066cc;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: #0055aa;
  }
`;

// ChatBot Component
function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [currentOptions, setCurrentOptions] = useState([]);
  const messagesEndRef = useRef(null);
  
  // Lead collection state
  const [collectingLeadInfo, setCollectingLeadInfo] = useState(false);
  const [currentLeadField, setCurrentLeadField] = useState('');
  const [suggestedEmail, setSuggestedEmail] = useState('');
  const [leadInfo, setLeadInfo] = useState({
    fullName: '',
    netIncome: '',
    age: '',
    email: '',
    phoneNumber: '',
    city: '',
    preferredLoanType: ''
  });

  // Eligibility calculator state
  const [calculatingEligibility, setCalculatingEligibility] = useState(false);
  const [currentEligibilityField, setCurrentEligibilityField] = useState('');
  const [eligibilityInfo, setEligibilityInfo] = useState({
    age: '',
    monthlyIncome: '',
    existingDebts: '',
    desiredLoanAmount: ''
  });

  // Add these states for loan application
  const [applyingForLoan, setApplyingForLoan] = useState(false);
  const [applicationStep, setApplicationStep] = useState('');
  const [applicationType, setApplicationType] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [applicationId, setApplicationId] = useState('');

  // Add these states for application status checking
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusStep, setStatusStep] = useState('');
  const [statusVerificationData, setStatusVerificationData] = useState('');
  
  // Add these states for complaint handling
  const [filingComplaint, setFilingComplaint] = useState(false);
  const [complaintStep, setComplaintStep] = useState('');
  const [complaintInfo, setComplaintInfo] = useState({
    category: '',
    description: '',
    priority: '',
    contactNumber: ''
  });
  const [ticketId, setTicketId] = useState('');

  // Add these states for FAQs
  const [browsingFaqs, setBrowsingFaqs] = useState(false);
  const [faqCategory, setFaqCategory] = useState('');

  // Mock database of applications for status checking
  const mockApplications = [
    { id: 'HBFC-123456', cnic: '12345-1234567-1', status: 'Under Review', details: 'Your application is currently being reviewed by our loan department.' },
    { id: 'HBFC-234567', cnic: '23456-2345678-2', status: 'Documents Missing', details: 'We need additional documents: Income tax returns for the last year.' },
    { id: 'HBFC-345678', cnic: '34567-3456789-3', status: 'Approved', details: 'Congratulations! Your loan has been approved. A representative will contact you shortly.' },
    { id: 'HBFC-456789', cnic: '45678-4567890-4', status: 'Processing', details: 'Your application is in the processing stage. Verification is in progress.' },
    { id: 'HBFC-567890', cnic: '56789-5678901-5', status: 'Rejected', details: 'We regret to inform you that your application could not be approved at this time.' }
  ];

  // Initial welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        text: "Welcome to HBFC Home Finance! I'm your virtual assistant. How can I help you today?",
        isBot: true,
        options: [
          "Loan Information",
          "Check Eligibility",
          "Apply for Loan",
          "Contact Support",
          "I have a question",
          "Check Application Status",
          "Complaints",
          "FAQs"
        ]
      };
      setMessages([welcomeMessage]);
      setCurrentOptions(welcomeMessage.options);
    }
  }, [isOpen, messages.length]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;
    
    // Add user message
    const userMessage = {
      text: inputValue,
      isBot: false
    };
    setMessages([...messages, userMessage]);
    
    // If we're checking complaint status
    if (filingComplaint && complaintStep === 'checkStatus') {
      setTicketId(inputValue);
      setTimeout(() => {
        checkComplaintStatus(inputValue);
      }, 500);
    }
    // If we're filing a complaint and waiting for description
    else if (filingComplaint && complaintStep === 'description') {
      const updatedComplaintInfo = { ...complaintInfo, description: inputValue };
      setComplaintInfo(updatedComplaintInfo);
      setTimeout(() => {
        askForContactNumber();
      }, 500);
    }
    // If we're filing a complaint and waiting for contact number
    else if (filingComplaint && complaintStep === 'contactNumber') {
      const updatedComplaintInfo = { ...complaintInfo, contactNumber: inputValue };
      setComplaintInfo(updatedComplaintInfo);
      setTimeout(() => {
        submitComplaint(updatedComplaintInfo);
      }, 500);
    }
    // If we're checking application status and waiting for data
    else if (checkingStatus && statusStep === 'enterData') {
      setStatusVerificationData(inputValue);
      setTimeout(() => {
        checkApplicationStatus(inputValue);
      }, 500);
    }
    // If we're searching through FAQs
    else if (browsingFaqs && faqCategory === '') {
      setTimeout(() => {
        searchFaqs(inputValue);
      }, 500);
    }
    // If we're collecting lead info, process the input differently
    else if (collectingLeadInfo) {
      processLeadInput(inputValue);
    } else if (calculatingEligibility) {
      processEligibilityInput(inputValue);
    } else {
      // Process user message and generate bot response
      setTimeout(() => {
        processUserMessage(inputValue);
      }, 500);
    }
    
    setInputValue('');
  };

  const handleOptionClick = (option) => {
    // Add user selection as a message
    const userMessage = {
      text: option,
      isBot: false
    };
    setMessages([...messages, userMessage]);
    
    // If we're collecting lead info for email confirmation
    if (collectingLeadInfo && currentLeadField === 'emailConfirmation') {
      if (option === "Yes, that's correct") {
        // Use the suggested email
        const updatedLeadInfo = { ...leadInfo, email: suggestedEmail };
        setLeadInfo(updatedLeadInfo);
        setSuggestedEmail('');
        
        setTimeout(() => {
          const confirmationResponse = {
            text: "✅ Email confirmed! What is your phone number?",
            isBot: true
          };
          setMessages(prevMessages => [...prevMessages, confirmationResponse]);
          setCurrentLeadField('phoneNumber');
        }, 500);
        return;
      } else if (option === "No, let me re-enter") {
        // User wants to re-enter email
        setSuggestedEmail('');
        setTimeout(() => {
          const reenterResponse = {
            text: "Please enter your correct email address:",
            isBot: true
          };
          setMessages(prevMessages => [...prevMessages, reenterResponse]);
          setCurrentLeadField('email');
        }, 500);
        return;
      }
    }
    
    // If we're collecting lead info for the preferred loan type
    if (collectingLeadInfo && currentLeadField === 'preferredLoanType') {
      // Update the lead info with the selected loan type
      const updatedLeadInfo = { ...leadInfo, preferredLoanType: option };
      setLeadInfo(updatedLeadInfo);
      
      // Complete the lead collection process
      // Show processing message first
      const processingMessage = {
        text: "Processing your information and sending confirmation email...",
        isBot: true
      };
      setMessages(prevMessages => [...prevMessages, processingMessage]);
      
      setTimeout(() => {
        completeLead();
      }, 1000);
      return;
    }
    
    // Process the selected option
    setTimeout(() => {
      processOption(option);
    }, 500);
  };

  const processUserMessage = (message) => {
    // Simple keyword-based responses for now
    const lowerMessage = message.toLowerCase();
    let botResponse = {
      text: "I'm not sure how to help with that. Could you please select one of the options below?",
      isBot: true,
      options: ["Loan Information", "Check Eligibility", "Apply for Loan", "Contact Support", "I have a question", "Check Application Status", "Complaints", "FAQs"]
    };
    
    if (lowerMessage.includes('loan') || lowerMessage.includes('finance') || lowerMessage.includes('mortgage')) {
      botResponse = {
        text: "We offer various home financing solutions. What would you like to know more about?",
        isBot: true,
        options: ["Home Purchase Loan", "Home Construction Loan", "Home Renovation Loan"]
      };
    } else if (lowerMessage.includes('eligibility') || lowerMessage.includes('qualify')) {
      startEligibilityCalculation();
      return;
    } else if (lowerMessage.includes('apply') || lowerMessage.includes('application')) {
      botResponse = {
        text: "Great! To start your loan application, please select the type of loan you're interested in:",
        isBot: true,
        options: ["Home Purchase Loan", "Home Construction Loan", "Home Renovation Loan"]
      };
    } else if (lowerMessage.includes('status') || lowerMessage.includes('track')) {
      startStatusCheck();
      return;
    } else if (lowerMessage.includes('complaint') || lowerMessage.includes('issue') || lowerMessage.includes('problem')) {
      startComplaintOptions();
      return;
    } else if (lowerMessage.includes('faq') || lowerMessage.includes('question') || lowerMessage.includes('how to')) {
      showFaqCategories();
      return;
    } else if (lowerMessage.includes('contact') || lowerMessage.includes('support') || lowerMessage.includes('help')) {
      botResponse = {
        text: "Our support team is available Monday to Friday, 9 AM to 5 PM. How would you like to reach us?",
        isBot: true,
        options: ["Phone", "Email", "Visit Branch"]
      };
    } else if (lowerMessage.includes('question')) {
      startLeadCollection();
      return;
    }
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    setCurrentOptions(botResponse.options || []);
  };

  const processOption = (option) => {
    let botResponse = {
      text: "I'm processing your request...",
      isBot: true
    };
    
    switch (option) {
      case "I have a question":
        startLeadCollection();
        return;
        
      case "Check Eligibility":
        startEligibilityCalculation();
        return;
        
      case "Complaints":
        startComplaintOptions();
        return;
        
      case "FAQs":
        showFaqCategories();
        return;
        
      case "General Finance Questions":
      case "Loan Policies":
      case "Service Timelines":
      case "Platform Usage":
        if (browsingFaqs) {
          setFaqCategory(option);
          showFaqQuestions(option);
          return;
        }
        break;
        
      case "Back to FAQ Categories":
        showFaqCategories();
        return;
        
      case "Search All FAQs":
        promptFaqSearch();
        return;
        
      case "Check Application Status":
        startStatusCheck();
        return;
        
      case "File a New Complaint":
        startComplaintFiling();
        return;
        
      case "Track Existing Complaint":
        askForTicketId();
        return;
        
      case "Loan Information":
        botResponse = {
          text: "What type of loan information are you looking for?",
          isBot: true,
          options: ["Home Purchase Loan", "Home Construction Loan", "Home Renovation Loan", "Loan Rates & Fees"]
        };
        break;
        
      case "Home Purchase Loan":
        botResponse = {
          text: "Our Home Purchase Loan helps you buy your dream home with competitive rates. Features include:\n• Financing up to 85% of property value\n• Tenure up to 25 years\n• Competitive markup rates starting from 12%\n• Minimal processing fee\n\nWhat else would you like to know?",
          isBot: true,
          options: ["Eligibility Criteria", "Required Documents", "Application Process", "Back to Main Menu"]
        };
        break;
        
      case "Home Construction Loan":
        botResponse = {
          text: "Our Home Construction Loan helps you build your dream home. Features include:\n• Stage-wise disbursement based on construction progress\n• Technical assistance throughout the construction\n• Flexible payment options\n• Tenure up to 20 years\n\nWhat else would you like to know?",
          isBot: true,
          options: ["Eligibility Criteria", "Required Documents", "Application Process", "Back to Main Menu"]
        };
        break;
        
      case "Home Renovation Loan":
        botResponse = {
          text: "Our Home Renovation Loan helps you upgrade your existing home. Features include:\n• Quick approval process\n• Minimal documentation\n• Affordable installments\n• Tenure up to 10 years\n\nWhat else would you like to know?",
          isBot: true,
          options: ["Eligibility Criteria", "Required Documents", "Application Process", "Back to Main Menu"]
        };
        break;
        
      case "Loan Rates & Fees":
        botResponse = {
          text: "Current rates and fees for HBFC loans:\n\nMarkup Rates:\n• Home Purchase: 12% - 14% p.a.\n• Home Construction: 12.5% - 14.5% p.a.\n• Home Renovation: 13% - 15% p.a.\n\nFees:\n• Processing fee: 1% of loan amount\n• Documentation charges: PKR 5,000\n• Property valuation: PKR 10,000 - 25,000\n• Legal charges: PKR 15,000 - 30,000\n\nNote: Rates and fees may vary based on loan amount, tenure, and customer profile.",
          isBot: true,
          options: ["Apply for Loan", "Check Eligibility", "Back to Main Menu"]
        };
        break;
        
      case "Eligibility Criteria":
        botResponse = {
          text: "General eligibility criteria for HBFC loans:\n• Pakistani national\n• Age: 18-65 years (at loan maturity)\n• Minimum income: PKR 50,000/month\n• Clean credit history\n• Debt-to-income ratio below 50%\n• Property should be in an approved location\n\nWould you like to check your eligibility?",
          isBot: true,
          options: ["Check Eligibility", "Apply for Loan", "Back to Main Menu"]
        };
        break;
        
      case "Required Documents":
        botResponse = {
          text: "Standard documents required for all loan applications:\n• CNIC (front and back)\n• Proof of income\n• Bank statements (6-12 months)\n• Property documents\n• Two passport-sized photographs\n• Utility bills as address proof\n\nAdditional documents may be required based on your employment type and loan type.",
          isBot: true,
          options: ["Apply for Loan", "Back to Main Menu"]
        };
        break;
        
      case "Application Process":
        botResponse = {
          text: "The loan application process includes:\n1. Submit application form with required documents\n2. Initial assessment (1-2 days)\n3. Property valuation (3-5 days)\n4. Loan approval (5-7 days)\n5. Documentation and legal formalities (7-10 days)\n6. Disbursement (3-5 days)\n\nTotal process typically takes 15-30 days depending on documentation completeness.",
          isBot: true,
          options: ["Apply for Loan", "Back to Main Menu"]
        };
        break;
        
      case "Apply for Loan":
        botResponse = {
          text: "To start your loan application, I'll need to collect some basic information. Would you like to proceed?",
          isBot: true,
          options: ["Yes, start application", "No, maybe later"]
        };
        break;
        
      case "Yes, start application":
        startLoanApplication();
        return;
        
      case "No, maybe later":
        botResponse = {
          text: "No problem! You can start your application whenever you're ready. Is there anything else I can help you with?",
          isBot: true,
          options: ["Loan Information", "Check Eligibility", "Back to Main Menu"]
        };
        break;
        
      case "Contact Support":
        botResponse = {
          text: "You can reach our support team through:\n• Phone: +92-123-456789\n• Email: support@hbfc.com\n• Visit any of our branches nationwide\n\nHow would you like to proceed?",
          isBot: true,
          options: ["Request Callback", "Find Nearest Branch", "Back to Main Menu"]
        };
        break;
        
      case "Phone":
      case "Request Callback":
        botResponse = {
          text: "Our customer service team will call you back shortly. Our operating hours are 9 AM to 5 PM, Monday to Friday. Please note that callbacks are typically scheduled within 24 hours.",
          isBot: true,
          options: ["Back to Main Menu"]
        };
        break;
        
      case "Email":
        botResponse = {
          text: "You can email your queries to support@hbfc.com. Our team typically responds within 24-48 hours. Please include your contact details in the email for faster assistance.",
          isBot: true,
          options: ["Back to Main Menu"]
        };
        break;
        
      case "Speak with Financial Advisor":
        botResponse = {
          text: "I'll connect you with one of our financial advisors who can provide personalized guidance for your home financing needs. Please provide your contact number and preferred time for a callback, and our advisor will reach out to you within 24 hours.",
          isBot: true,
          options: ["Back to Main Menu"]
        };
        break;
        
      case "Visit Branch":
      case "Find Nearest Branch":
        botResponse = {
          text: "Our branches are located in major cities across Pakistan. To find the nearest branch, please visit our website: https://hbfc.com/branches\n\nOur branches are open from 9 AM to 5 PM, Monday to Friday, and 9 AM to 1 PM on Saturdays.",
          isBot: true,
          options: ["Back to Main Menu"]
        };
        break;
        
      case "Back to Main Menu":
        botResponse = {
          text: "How else can I help you today?",
          isBot: true,
          options: ["Loan Information", "Check Eligibility", "Apply for Loan", "Contact Support", "I have a question", "Check Application Status", "Complaints", "FAQs"]
        };
        setBrowsingFaqs(false);
        setFaqCategory('');
        break;
        
      // Add new case statements for loan application flow
      case "Home Purchase":
      case "Construction":
      case "Renovation":
      case "Balance Transfer":
        if (applyingForLoan && applicationStep === 'loanType') {
          setApplicationType(option);
          askEmploymentType();
          return;
        }
        break;
        
      case "Salaried individual":
      case "Self-employed":
      case "Business owner":
        if (applyingForLoan && applicationStep === 'employmentType') {
          setEmploymentType(option);
          showDocumentRequirements(option);
          return;
        }
        break;
        
      case "Upload Documents":
        if (applyingForLoan && applicationStep === 'documents') {
          provideUploadLink();
          return;
        }
        break;
        
      case "I'll do it later":
        if (applyingForLoan) {
          botResponse = {
            text: "No problem! You can continue your application later. Your information has been saved. Is there anything else I can help you with?",
            isBot: true,
            options: ["Back to Main Menu", "Check Eligibility", "Loan Information"]
          };
          resetLoanApplication();
        }
        break;
        
      case "Complete Application":
        if (applyingForLoan && applicationStep === 'upload') {
          completeApplication();
          return;
        }
        break;
        
      case "Check Another Application":
        startStatusCheck();
        return;
        
      case "Try Again":
        startStatusCheck();
        return;
        
      // ... existing cases ...
        
      default:
        // Check if this is a FAQ question
        if (browsingFaqs && faqCategory) {
          const faqAnswer = getFaqAnswer(option, faqCategory);
          if (faqAnswer) {
            botResponse = {
              text: faqAnswer,
              isBot: true,
              options: ["Back to FAQ Categories", "Back to Main Menu"]
            };
          } else {
            botResponse = {
              text: "I'm still learning about that topic. Is there something else I can help you with?",
              isBot: true,
              options: ["Back to FAQ Categories", "Back to Main Menu"]
            };
          }
        } else {
          botResponse = {
            text: "I'm still learning about that topic. Is there something else I can help you with?",
            isBot: true,
            options: ["Loan Information", "Check Eligibility", "Apply for Loan", "Contact Support", "I have a question", "Check Application Status", "Complaints", "FAQs"]
          };
        }
    }
    
    // Handle complaint category selection
    if (filingComplaint && complaintStep === 'category') {
      if (['Technical Issues', 'Disbursement Issues', 'Collection Issues', 'Other'].includes(option)) {
        const updatedComplaintInfo = { ...complaintInfo, category: option };
        setComplaintInfo(updatedComplaintInfo);
        askForDescription();
        return;
      }
    }
    
    // Handle complaint priority selection
    if (filingComplaint && complaintStep === 'priority') {
      if (['High', 'Medium', 'Low'].includes(option)) {
        const updatedComplaintInfo = { ...complaintInfo, priority: option };
        setComplaintInfo(updatedComplaintInfo);
        checkComplaintStatus(ticketId);
        return;
      }
    }
    
    // Handle complaint escalation
    if (filingComplaint && complaintStep === 'escalation') {
      if (['Escalate Complaint', 'View Status'].includes(option)) {
        if (option === 'Escalate Complaint') {
          escalateComplaint();
        } else {
          checkComplaintStatus(ticketId);
        }
        return;
      }
    }
    
    // Handle status verification input
    if (checkingStatus) {
      if (statusStep === 'selectMethod' && (option === 'Application ID' || option === 'CNIC Number')) {
        askForVerificationData(option);
        return;
      }
    }
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    setCurrentOptions(botResponse.options || []);
  };

  // Lead collection functions
  const startLeadCollection = () => {
    setCollectingLeadInfo(true);
    setCurrentLeadField('fullName');
    
    const botResponse = {
      text: "I'd be happy to help with your question. First, I'll need to collect some information to connect you with a sales representative. Could you please provide your full name?",
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    setCurrentOptions([]);
  };

  const processLeadInput = (input) => {
    // Update the lead info with the current input
    const updatedLeadInfo = { ...leadInfo };
    updatedLeadInfo[currentLeadField] = input;
    setLeadInfo(updatedLeadInfo);
    
    // Determine the next field to collect
    let nextField = '';
    let nextPrompt = '';
    
    switch (currentLeadField) {
      case 'fullName':
        nextField = 'netIncome';
        nextPrompt = "Thank you! What is your monthly net income? (in PKR)";
        break;
        
      case 'netIncome':
        nextField = 'age';
        nextPrompt = "What is your age?";
        break;
        
      case 'age':
        nextField = 'email';
        nextPrompt = "Please provide your email address:";
        break;
        
      case 'email':
        // Enhanced email validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const trimmedEmail = input.trim().toLowerCase();
        
        if (!emailRegex.test(trimmedEmail)) {
          const invalidEmailResponse = {
            text: "❌ Please enter a valid email address.\n\nValid format: yourname@domain.com\nExamples: john@gmail.com, user@company.co.uk\n\nPlease try again:",
            isBot: true
          };
          setMessages(prevMessages => [...prevMessages, invalidEmailResponse]);
          return;
        }
        
        // Additional validation checks
        if (trimmedEmail.length > 254) {
          const tooLongEmailResponse = {
            text: "❌ Email address is too long. Please enter a shorter email address:",
            isBot: true
          };
          setMessages(prevMessages => [...prevMessages, tooLongEmailResponse]);
          return;
        }
        
        // Check for common typos in popular domains
        const commonDomainTypos = {
          'gmail.co': 'gmail.com',
          'gmail.cm': 'gmail.com',
          'gmai.com': 'gmail.com',
          'yahoo.co': 'yahoo.com',
          'yahoo.cm': 'yahoo.com',
          'hotmail.co': 'hotmail.com',
          'hotmail.cm': 'hotmail.com',
          'outlook.co': 'outlook.com',
          'outlook.cm': 'outlook.com'
        };
        
        const emailDomain = trimmedEmail.split('@')[1];
        if (commonDomainTypos[emailDomain]) {
          const suggestedEmail = trimmedEmail.replace(emailDomain, commonDomainTypos[emailDomain]);
          const typoSuggestionResponse = {
            text: `Did you mean: ${suggestedEmail}?\n\nIf yes, please type "yes". If no, please enter the correct email address:`,
            isBot: true,
            options: ["Yes, that's correct", "No, let me re-enter"]
          };
          setMessages(prevMessages => [...prevMessages, typoSuggestionResponse]);
          
          // Store the suggested email for potential use
          setSuggestedEmail(suggestedEmail);
          setCurrentLeadField('emailConfirmation');
          return;
        }
        
        // Store the validated email
        updatedLeadInfo[currentLeadField] = trimmedEmail;
        nextField = 'phoneNumber';
        nextPrompt = "✅ Email confirmed! What is your phone number?";
        break;
        
      case 'emailConfirmation':
        // Handle email typo correction confirmation
        if (input.toLowerCase() === 'yes' || input.toLowerCase() === 'y') {
          // Use the suggested email
          updatedLeadInfo['email'] = suggestedEmail;
          setLeadInfo(updatedLeadInfo);
          setSuggestedEmail('');
          
          const confirmationResponse = {
            text: "✅ Email confirmed! What is your phone number?",
            isBot: true
          };
          setMessages(prevMessages => [...prevMessages, confirmationResponse]);
          setCurrentLeadField('phoneNumber');
          return;
        } else {
          // User wants to re-enter email
          setSuggestedEmail('');
          const reenterResponse = {
            text: "Please enter your correct email address:",
            isBot: true
          };
          setMessages(prevMessages => [...prevMessages, reenterResponse]);
          setCurrentLeadField('email');
          return;
        }
        
      case 'phoneNumber':
        nextField = 'city';
        nextPrompt = "Which city do you live in?";
        break;
        
      case 'city':
        nextField = 'preferredLoanType';
        nextPrompt = "What type of loan are you interested in?";
        
        // Send the next prompt with loan type options
        const botResponse = {
          text: nextPrompt,
          isBot: true,
          options: ["Home Purchase", "Construction", "Renovation", "Balance Transfer"]
        };
        
        setMessages(prevMessages => [...prevMessages, botResponse]);
        setCurrentLeadField(nextField);
        return;
        
      default:
        // Reset to main menu if there's an error
        resetLeadCollection();
        return;
    }
    
    // Update the current field and send the next prompt
    setCurrentLeadField(nextField);
    
    const botResponse = {
      text: nextPrompt,
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const completeLead = async () => {
    // In a real application, you would send this data to your backend
    console.log("Lead collected:", leadInfo);
    
    try {
      // Final email validation before sending
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!leadInfo.email || !emailRegex.test(leadInfo.email)) {
        console.error('Invalid email format detected:', leadInfo.email);
        throw new Error('Invalid email format');
      }
      
      // Send confirmation email to the user with all collected details
      const emailResult = await sendEmail(leadInfo.email, 'leadConfirmation', leadInfo);
      
      let confirmationMessage;
      if (emailResult.success) {
        confirmationMessage = "Thank you for providing your information. Your details have been sent to a sales representative, and they will be contacting you soon. We've also sent a confirmation email to your provided email address.";
      } else {
        confirmationMessage = "Thank you for providing your information. Your details have been sent to a sales representative, and they will be contacting you soon. (Note: There was an issue sending the confirmation email, but your inquiry has been recorded.)";
        console.error('Email sending failed:', emailResult.error);
      }
      
      // Send confirmation message
      const botResponse = {
        text: confirmationMessage,
        isBot: true,
        options: ["Back to Main Menu"]
      };
      
      setMessages(prevMessages => [...prevMessages, botResponse]);
      
    } catch (error) {
      console.error('Error in completeLead:', error);
      
      // Fallback message if email service fails
      const botResponse = {
        text: "Thank you for providing your information. Your details have been sent to a sales representative, and they will be contacting you soon.",
        isBot: true,
        options: ["Back to Main Menu"]
      };
      
      setMessages(prevMessages => [...prevMessages, botResponse]);
    }
    
    // Reset lead collection state
    resetLeadCollection();
  };

  const resetLeadCollection = () => {
    setCollectingLeadInfo(false);
    setCurrentLeadField('');
    setSuggestedEmail('');
    setLeadInfo({
      fullName: '',
      netIncome: '',
      age: '',
      email: '',
      phoneNumber: '',
      city: '',
      preferredLoanType: ''
    });
  };

  // Eligibility Calculator functions
  const startEligibilityCalculation = () => {
    setCalculatingEligibility(true);
    setCurrentEligibilityField('age');
    
    const botResponse = {
      text: "Let's calculate your loan eligibility. I'll need a few details from you. To start, what is your age?",
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    setCurrentOptions([]);
  };

  const processEligibilityInput = (input) => {
    // Update the eligibility info with the current input
    const updatedEligibilityInfo = { ...eligibilityInfo };
    updatedEligibilityInfo[currentEligibilityField] = input;
    setEligibilityInfo(updatedEligibilityInfo);
    
    // Determine the next field to collect
    let nextField = '';
    let nextPrompt = '';
    
    switch (currentEligibilityField) {
      case 'age':
        nextField = 'monthlyIncome';
        nextPrompt = "What is your monthly income? (in PKR)";
        break;
        
      case 'monthlyIncome':
        nextField = 'existingDebts';
        nextPrompt = "What are your existing monthly debt payments? (in PKR)";
        break;
        
      case 'existingDebts':
        nextField = 'desiredLoanAmount';
        nextPrompt = "What loan amount are you looking for? (in PKR)";
        break;
        
      case 'desiredLoanAmount':
        // All information collected, calculate eligibility
        calculateEligibility(updatedEligibilityInfo);
        return;
        
      default:
        // Reset to main menu if there's an error
        resetEligibilityCalculation();
        return;
    }
    
    // Update the current field and send the next prompt
    setCurrentEligibilityField(nextField);
    
    const botResponse = {
      text: nextPrompt,
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const calculateEligibility = (info) => {
    // In a real application, this would be a more complex calculation
    // For this mock implementation, we'll use a simple formula
    
    // Parse input values
    const age = parseInt(info.age);
    const monthlyIncome = parseInt(info.monthlyIncome.replace(/[^0-9]/g, ''));
    const existingDebts = parseInt(info.existingDebts.replace(/[^0-9]/g, '') || '0');
    const desiredLoanAmount = parseInt(info.desiredLoanAmount.replace(/[^0-9]/g, ''));
    
    // Calculate debt-to-income ratio (DTI)
    const maxAllowableDTI = 0.5; // 50% of income
    const currentDTI = existingDebts / monthlyIncome;
    
    // Calculate maximum loan amount based on income
    // Assuming 40% of income can go towards this loan for 20 years at 12% markup
    const interestRate = 0.12; // 12% annual
    const loanTermYears = 20;
    const monthlyRate = interestRate / 12;
    const totalPayments = loanTermYears * 12;
    
    // Maximum monthly payment the applicant can afford
    const maxMonthlyPayment = monthlyIncome * maxAllowableDTI - existingDebts;
    
    // Maximum loan amount based on affordability
    // PMT formula: P = M * [(1 - (1 + r)^-n) / r]
    // where P is principal, M is monthly payment, r is monthly rate, n is number of payments
    const maxLoanAmount = maxMonthlyPayment * ((1 - Math.pow(1 + monthlyRate, -totalPayments)) / monthlyRate);
    
    // Adjust based on age (simplified)
    let ageAdjustedLoanAmount = maxLoanAmount;
    if (age > 45) {
      // Reduce loan term for older applicants
      const reducedTermYears = Math.max(5, 25 - ((age - 45) / 2));
      const reducedPayments = reducedTermYears * 12;
      ageAdjustedLoanAmount = maxMonthlyPayment * ((1 - Math.pow(1 + monthlyRate, -reducedPayments)) / monthlyRate);
    }
    
    // Final eligible amount (considering desired amount)
    const eligibleAmount = Math.min(ageAdjustedLoanAmount, desiredLoanAmount * 0.85); // 85% of property value
    
    // Calculate monthly installment for the eligible amount
    const monthlyInstallment = eligibleAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1);
    
    // Format numbers for display
    const formattedEligibleAmount = Math.round(eligibleAmount).toLocaleString('en-US');
    const formattedMonthlyInstallment = Math.round(monthlyInstallment).toLocaleString('en-US');
    
    // Determine eligibility status
    let eligibilityStatus;
    let recommendations = '';
    
    if (eligibleAmount >= desiredLoanAmount * 0.8) {
      eligibilityStatus = "You are likely eligible for your desired loan amount.";
    } else if (eligibleAmount >= desiredLoanAmount * 0.5) {
      eligibilityStatus = "You are partially eligible for your desired loan amount.";
      recommendations = "To increase your eligibility, consider:\n• Reducing existing debts\n• Adding a co-applicant\n• Extending the loan tenure";
    } else {
      eligibilityStatus = "Based on the information provided, you may have limited eligibility.";
      recommendations = "To increase your eligibility, you might need to:\n• Significantly reduce existing debts\n• Add a co-applicant with strong income\n• Consider a smaller loan amount\n• Increase your down payment";
    }
    
    // Send the eligibility results
    const resultText = `Based on the information you provided:

${eligibilityStatus}

Estimated eligible loan amount: PKR ${formattedEligibleAmount}
Estimated monthly installment: PKR ${formattedMonthlyInstallment}

${recommendations}

Would you like to proceed with a loan application or speak with a financial advisor for more detailed guidance?`;
    
    const botResponse = {
      text: resultText,
      isBot: true,
      options: ["Apply for Loan", "Speak with Financial Advisor", "Back to Main Menu"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    
    // Reset eligibility calculation state
    resetEligibilityCalculation();
  };

  const resetEligibilityCalculation = () => {
    setCalculatingEligibility(false);
    setCurrentEligibilityField('');
    setEligibilityInfo({
      age: '',
      monthlyIncome: '',
      existingDebts: '',
      desiredLoanAmount: ''
    });
  };

  // Loan Application functions
  const startLoanApplication = () => {
    setApplyingForLoan(true);
    setApplicationStep('loanType');
    
    const botResponse = {
      text: "Great! Let's start your loan application. First, please select the type of loan you're interested in:",
      isBot: true,
      options: ["Home Purchase", "Construction", "Renovation", "Balance Transfer"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const askEmploymentType = () => {
    setApplicationStep('employmentType');
    
    const botResponse = {
      text: "Please select your employment status:",
      isBot: true,
      options: ["Salaried individual", "Self-employed", "Business owner"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const showDocumentRequirements = (empType) => {
    setApplicationStep('documents');
    
    let documentsText = "Based on your employment status as a ";
    let documentsList = [];
    
    switch (empType) {
      case "Salaried individual":
        documentsList = [
          "CNIC (front and back)",
          "Salary slips for the last 3 months",
          "Bank statements for the last 6 months",
          "Employment verification letter",
          "Property documents related to your loan request",
          "Utility bills (recent)"
        ];
        break;
        
      case "Self-employed":
        documentsList = [
          "CNIC (front and back)",
          "Bank statements for the last 12 months",
          "Income tax returns for the last 2 years",
          "Business proof documents (registration, licenses)",
          "Property documents related to your loan request",
          "Utility bills (recent)"
        ];
        break;
        
      case "Business owner":
        documentsList = [
          "CNIC (front and back)",
          "Bank statements for the last 12 months",
          "Business financial statements",
          "Company registration documents",
          "Income tax returns for the last 2 years",
          "Property documents related to your loan request",
          "Utility bills (recent)"
        ];
        break;
        
      default:
        documentsList = [
          "CNIC (front and back)",
          "Income proof documents",
          "Property documents related to your loan request"
        ];
    }
    
    documentsText += `${empType}, here's what you'll need to submit:`;
    
    // Format the documents as a bullet list
    const formattedList = documentsList.map(doc => `• ${doc}`).join("\n");
    
    const botResponse = {
      text: `${documentsText}\n\n${formattedList}\n\nWould you like to upload these documents now?`,
      isBot: true,
      options: ["Upload Documents", "I'll do it later"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const provideUploadLink = () => {
    setApplicationStep('upload');
    
    const botResponse = {
      text: "You can upload your documents using the secure link below:\n\nhttps://hbfc.com/document-upload\n\nOnce you've uploaded all required documents, please click 'Complete Application' to finalize your application.",
      isBot: true,
      options: ["Complete Application", "I'll do it later"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const completeApplication = () => {
    // Generate a random application ID
    const newApplicationId = "HBFC-" + Math.floor(100000 + Math.random() * 900000);
    setApplicationId(newApplicationId);
    
    const botResponse = {
      text: `Thank you for completing your application! Your application has been submitted successfully.\n\nYour application tracking ID is: ${newApplicationId}\n\nYou can use this ID to check the status of your application. A loan officer will review your application and contact you within 2-3 business days.\n\nIs there anything else I can help you with?`,
      isBot: true,
      options: ["Check Application Status", "Back to Main Menu"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    
    // Reset application state
    resetLoanApplication();
  };

  const resetLoanApplication = () => {
    setApplyingForLoan(false);
    setApplicationStep('');
    setApplicationType('');
    setEmploymentType('');
  };

  // Application Status Check functions
  const startStatusCheck = () => {
    setCheckingStatus(true);
    setStatusStep('selectMethod');
    
    const botResponse = {
      text: "I'd be happy to help you check your application status. How would you like to verify your identity?",
      isBot: true,
      options: ["Application ID", "CNIC Number"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const askForVerificationData = (method) => {
    setStatusStep('enterData');
    
    let promptText = "";
    if (method === 'Application ID') {
      promptText = "Please enter your Application ID (format: HBFC-XXXXXX):";
    } else {
      promptText = "Please enter your CNIC Number (format: XXXXX-XXXXXXX-X):";
    }
    
    const botResponse = {
      text: promptText,
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const checkApplicationStatus = (verificationData) => {
    // Clean up the verification data
    const cleanData = verificationData.trim().toUpperCase();
    
    // Find the application in our mock database
    let foundApplication = null;
    
    if (cleanData.startsWith('HBFC-')) {
      // Search by Application ID
      foundApplication = mockApplications.find(app => app.id === cleanData);
    } else {
      // Search by CNIC
      foundApplication = mockApplications.find(app => app.cnic === cleanData);
    }
    
    let botResponse;
    
    if (foundApplication) {
      // Found the application
      const statusColor = getStatusColor(foundApplication.status);
      
      botResponse = {
        text: `Application Status: ${foundApplication.status}\n\n${foundApplication.details}\n\nApplication ID: ${foundApplication.id}\nLast Updated: ${new Date().toLocaleDateString()}\n\nIf you have any questions about your application, please contact our support team.`,
        isBot: true,
        options: ["Check Another Application", "Back to Main Menu"]
      };
    } else {
      // Application not found
      botResponse = {
        text: "I couldn't find an application with the provided information. Please check the information and try again.",
        isBot: true,
        options: ["Try Again", "Back to Main Menu"]
      };
    }
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    
    // Reset the status check if the application was found
    if (foundApplication) {
      resetStatusCheck();
    } else {
      // If not found, stay in the status check flow
      setStatusStep('selectMethod');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'green';
      case 'Under Review':
      case 'Processing':
        return 'blue';
      case 'Documents Missing':
        return 'orange';
      case 'Rejected':
        return 'red';
      default:
        return 'grey';
    }
  };

  const resetStatusCheck = () => {
    setCheckingStatus(false);
    setStatusStep('');
    setStatusVerificationData('');
  };

  // Complaint Filing functions
  const startComplaintFiling = () => {
    setFilingComplaint(true);
    setComplaintStep('category');
    
    const botResponse = {
      text: "I'm sorry to hear you're experiencing an issue. Let's file a complaint to get it resolved. Please select the category that best describes your issue:",
      isBot: true,
      options: ["Technical Issues", "Disbursement Issues", "Collection Issues", "Other"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const askForDescription = () => {
    setComplaintStep('description');
    
    const botResponse = {
      text: "Please describe your issue in detail. Include any relevant information that might help us resolve it:",
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const askForContactNumber = () => {
    setComplaintStep('contactNumber');
    
    const botResponse = {
      text: "Please provide a contact number where our team can reach you regarding this complaint:",
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const submitComplaint = (info) => {
    // Generate a ticket ID
    const newTicketId = "COMP-" + Math.floor(100000 + Math.random() * 900000);
    setTicketId(newTicketId);
    
    // Determine department based on category
    let department = "";
    switch (info.category) {
      case "Technical Issues":
        department = "Technical Support";
        break;
      case "Disbursement Issues":
        department = "Disbursement Department";
        break;
      case "Collection Issues":
        department = "Collection Department";
        break;
      default:
        department = "Customer Support";
    }
    
    // Determine expected resolution time based on severity
    const currentDate = new Date();
    let resolutionDate = new Date(currentDate);
    resolutionDate.setDate(currentDate.getDate() + 3); // Default 3 days
    
    const formattedResolutionDate = resolutionDate.toLocaleDateString();
    
    // Confirmation message
    const botResponse = {
      text: `Thank you for submitting your complaint.\n\nTicket ID: ${newTicketId}\nCategory: ${info.category}\nRouted to: ${department}\nExpected Resolution: ${formattedResolutionDate}\n\nA representative from the ${department} will contact you at ${info.contactNumber} to resolve your issue. You can check the status of your complaint using your Ticket ID.\n\nHow would you rate the priority of this issue?`,
      isBot: true,
      options: ["High", "Medium", "Low"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    setComplaintStep('priority');
  };

  const checkComplaintStatus = (id) => {
    // In a real application, this would query a database
    // For this mock, we'll simulate different statuses
    
    // Generate a random status for demonstration
    const statuses = ["Open", "In Progress", "Pending Customer Feedback", "Resolved", "Closed"];
    const randomStatus = statuses[Math.floor(Math.random() * 3)]; // Only first 3 to avoid resolved/closed
    
    let botResponse;
    
    if (randomStatus === "Open" || randomStatus === "Pending Customer Feedback") {
      // If still open or pending, offer escalation
      botResponse = {
        text: `Complaint Status: ${randomStatus}\n\nTicket ID: ${id}\n\nYour complaint is ${randomStatus.toLowerCase()} and is being reviewed by our team. The current status indicates it has not been resolved yet. Would you like to escalate this complaint to a supervisor?`,
        isBot: true,
        options: ["Escalate Complaint", "Back to Main Menu"]
      };
      setComplaintStep('escalation');
    } else {
      // If in progress or other status
      botResponse = {
        text: `Complaint Status: ${randomStatus}\n\nTicket ID: ${id}\n\nYour complaint is ${randomStatus.toLowerCase()} and is being worked on by our team. Please allow some time for resolution. If your issue is urgent, you can escalate it to a supervisor.`,
        isBot: true,
        options: ["Escalate Complaint", "Back to Main Menu"]
      };
      setComplaintStep('escalation');
    }
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const escalateComplaint = () => {
    // Simulate escalation process
    const escalationId = "ESC-" + Math.floor(1000 + Math.random() * 9000);
    
    const botResponse = {
      text: `Your complaint has been escalated to a supervisor.\n\nEscalation ID: ${escalationId}\nOriginal Ticket ID: ${ticketId}\n\nA supervisor will review your case and contact you within 24 hours. Thank you for your patience.`,
      isBot: true,
      options: ["Back to Main Menu"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
    resetComplaintFiling();
  };

  const resetComplaintFiling = () => {
    setFilingComplaint(false);
    setComplaintStep('');
    setComplaintInfo({
      category: '',
      description: '',
      priority: '',
      contactNumber: ''
    });
    // Don't reset ticketId to allow for future reference
  };

  const askForTicketId = () => {
    setFilingComplaint(true);
    setComplaintStep('checkStatus');
    
    const botResponse = {
      text: "To check the status of your complaint, please enter your Ticket ID (format: COMP-XXXXXX):",
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  // New function to show complaint options
  const startComplaintOptions = () => {
    setFilingComplaint(true);
    setComplaintStep('options');
    
    const botResponse = {
      text: "What would you like to do with regards to complaints?",
      isBot: true,
      options: ["File a New Complaint", "Track Existing Complaint"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  // FAQ functions
  const showFaqCategories = () => {
    setBrowsingFaqs(true);
    setFaqCategory('');
    
    const botResponse = {
      text: "Welcome to our FAQ section. Please select a category you'd like to explore:",
      isBot: true,
      options: [
        "General Finance Questions",
        "Loan Policies",
        "Service Timelines",
        "Platform Usage",
        "Search All FAQs"
      ]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const showFaqQuestions = (category) => {
    let questions = [];
    
    switch (category) {
      case "General Finance Questions":
        questions = [
          "What is markup/interest rate?",
          "How is loan amount calculated?",
          "What is the difference between fixed and variable rates?",
          "How do installment payments work?",
          "What is debt-to-income ratio?",
          "What is loan tenure?"
        ];
        break;
        
      case "Loan Policies":
        questions = [
          "What is the minimum credit score required?",
          "Who is eligible for a home loan?",
          "Can I apply with a co-applicant?",
          "What happens if I miss a payment?",
          "Can I pay off my loan early?",
          "What property types qualify for financing?"
        ];
        break;
        
      case "Service Timelines":
        questions = [
          "How long does loan approval take?",
          "When will I receive funds after approval?",
          "How long does document verification take?",
          "How often can I refinance?",
          "How quickly can I get a statement for my loan?",
          "What are the turnaround times for inquiries?"
        ];
        break;
        
      case "Platform Usage":
        questions = [
          "How do I navigate the portal?",
          "How can I update my personal information?",
          "How do I check my loan balance?",
          "How do I download my payment history?",
          "How do I submit documents online?",
          "How do I set up automatic payments?"
        ];
        break;
        
      default:
        questions = ["No questions found for this category."];
    }
    
    const botResponse = {
      text: `Here are some common questions about ${category}:`,
      isBot: true,
      options: [...questions, "Back to FAQ Categories", "Back to Main Menu"]
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const promptFaqSearch = () => {
    const botResponse = {
      text: "Please type your question or keywords below, and I'll try to find the most relevant answers for you:",
      isBot: true
    };
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const searchFaqs = (query) => {
    // Simple keyword search through all FAQ answers
    const keywords = query.toLowerCase().split(' ');
    
    // Create a scoring system for matching FAQs
    const scoredMatches = [];
    
    // Get all FAQ categories and questions
    const allCategories = ["General Finance Questions", "Loan Policies", "Service Timelines", "Platform Usage"];
    
    allCategories.forEach(category => {
      // Get questions for this category
      let questions = [];
      
      switch (category) {
        case "General Finance Questions":
          questions = [
            "What is markup/interest rate?",
            "How is loan amount calculated?",
            "What is the difference between fixed and variable rates?",
            "How do installment payments work?",
            "What is debt-to-income ratio?",
            "What is loan tenure?"
          ];
          break;
          
        case "Loan Policies":
          questions = [
            "What is the minimum credit score required?",
            "Who is eligible for a home loan?",
            "Can I apply with a co-applicant?",
            "What happens if I miss a payment?",
            "Can I pay off my loan early?",
            "What property types qualify for financing?"
          ];
          break;
          
        case "Service Timelines":
          questions = [
            "How long does loan approval take?",
            "When will I receive funds after approval?",
            "How long does document verification take?",
            "How often can I refinance?",
            "How quickly can I get a statement for my loan?",
            "What are the turnaround times for inquiries?"
          ];
          break;
          
        case "Platform Usage":
          questions = [
            "How do I navigate the portal?",
            "How can I update my personal information?",
            "How do I check my loan balance?",
            "How do I download my payment history?",
            "How do I submit documents online?",
            "How do I set up automatic payments?"
          ];
          break;
          
        default:
          questions = [];
      }
      
      // Score each question for this category
      questions.forEach(question => {
        // Calculate score based on keyword matches
        let score = 0;
        const questionLower = question.toLowerCase();
        
        keywords.forEach(keyword => {
          if (keyword.length > 3 && questionLower.includes(keyword)) {
            score += 1;
          }
        });
        
        // Add if there's any match
        if (score > 0) {
          scoredMatches.push({
            category,
            question,
            score
          });
        }
      });
    });
    
    // Sort by score (highest first)
    scoredMatches.sort((a, b) => b.score - a.score);
    
    // Prepare response
    let botResponse;
    
    if (scoredMatches.length > 0) {
      // Take top 5 matches
      const topMatches = scoredMatches.slice(0, 5).map(match => match.question);
      
      botResponse = {
        text: "Here are the most relevant questions I found based on your search:",
        isBot: true,
        options: [...topMatches, "Back to FAQ Categories", "Back to Main Menu"]
      };
    } else {
      botResponse = {
        text: "I couldn't find any matches for your question. Would you like to browse our FAQ categories instead?",
        isBot: true,
        options: ["Back to FAQ Categories", "Back to Main Menu"]
      };
    }
    
    setMessages(prevMessages => [...prevMessages, botResponse]);
  };

  const getFaqAnswer = (question, category) => {
    // Map questions to answers
    const faqDatabase = {
      "What is markup/interest rate?": "Markup or interest rate is the cost of borrowing money, expressed as a percentage of the loan amount. HBFC offers both fixed and variable markup rates. Fixed rates remain the same throughout the loan term, while variable rates may change based on market conditions. Our current markup rates range from 11% to 15% depending on loan type and tenure.",
      
      "How is loan amount calculated?": "Your loan amount is calculated based on several factors:\n\n• Property value (max 85% financing)\n• Your income and repayment capacity\n• Your debt-to-income ratio (typically max 50%)\n• Your age and requested tenure\n• Credit history\n\nThe maximum loan amount is determined by what you can afford to repay while maintaining a healthy debt-to-income ratio.",
      
      "What is the difference between fixed and variable rates?": "Fixed markup rates remain constant throughout the loan tenure, making your monthly payments predictable. Variable rates fluctuate based on market conditions (usually tied to KIBOR rates). Variable rates often start lower than fixed rates but carry the risk of increasing if market rates rise. Fixed rates provide stability while variable rates might save you money if rates decrease.",
      
      "How do installment payments work?": "Your loan installment has two components: principal and markup. Early in your loan tenure, a larger portion goes toward markup and a smaller portion toward principal repayment. As time progresses, this ratio gradually shifts with more going toward principal. You can choose monthly, quarterly, or semi-annual payment schedules based on your income pattern. Payments can be made through bank transfers, checks, or direct debit.",
      
      "What is debt-to-income ratio?": "Debt-to-income (DTI) ratio is the percentage of your gross monthly income that goes toward paying debts. To calculate DTI, divide your total monthly debt payments by your gross monthly income, then multiply by 100. For home loans, we typically require your DTI (including the new loan payment) to be below 50%. A lower DTI indicates better financial health and higher loan approval chances.",
      
      "What is loan tenure?": "Loan tenure is the total repayment period for your loan. HBFC offers tenures from 3 years up to 25 years. Longer tenures result in lower monthly payments but higher total markup paid over the life of the loan. Maximum tenure may be limited by your age at maturity (typically loans must be fully repaid by age 65).",
      
      "What is the minimum credit score required?": "HBFC requires a minimum credit score of 650 from the Credit Information Bureau (CIB) for loan approval. However, applicants with scores above 700 generally receive better markup rates and higher approval chances. If you have no credit history, we may consider alternative criteria such as utility payment history and rental payment records.",
      
      "Who is eligible for a home loan?": "To be eligible for an HBFC home loan, you must:\n\n• Be a Pakistani citizen or overseas Pakistani\n• Be 21-65 years of age (at the time of loan maturity)\n• Have a regular income source (salaried, self-employed, or business)\n• Have a clean credit history\n• Meet minimum income requirements (depending on loan amount)\n• Own or be purchasing a legally approved property\n\nRequirements may vary based on loan type and amount.",
      
      "Can I apply with a co-applicant?": "Yes, you can apply with a co-applicant to increase your loan eligibility. Co-applicants can be immediate family members such as spouse, parents, siblings, or adult children. The combined income of all applicants is considered for loan calculation, potentially increasing your eligible loan amount. Both applicants share legal responsibility for loan repayment.",
      
      "What happens if I miss a payment?": "If you miss a payment:\n\n• A late payment fee will be charged (typically 2% of the installment amount)\n• You'll receive payment reminders via SMS, email, and calls\n• Continued missed payments may result in financial penalties\n• Your credit score may be negatively affected\n• After 90 days, legal recovery proceedings may begin\n\nIf you anticipate payment difficulties, contact us proactively to discuss hardship arrangements.",
      
      "Can I pay off my loan early?": "Yes, you can pay off your loan early through partial or full prepayment. Full prepayment allows you to close your loan by paying the remaining principal. Partial prepayment lets you pay a portion of the principal, reducing your loan balance and subsequent installments. Early payment charges may apply, typically 2% for fixed-rate loans and 1% for variable-rate loans. These charges are waived after 5 years for most loan types.",
      
      "What property types qualify for financing?": "HBFC finances the following property types:\n\n• Residential houses (completed or under construction)\n• Apartments and flats in approved buildings\n• Residential plots for construction\n• Commercial properties (for commercial loans)\n\nThe property must have clear title, proper documentation, and comply with local building regulations. Properties in unapproved societies or with legal disputes are not eligible.",
      
      "How long does loan approval take?": "The standard loan approval process takes 15-21 working days after submission of complete documentation. This includes:\n\n• Initial assessment: 2-3 days\n• Property valuation: 3-5 days\n• Documentation verification: 5-7 days\n• Credit approval: 3-5 days\n• Final offer letter: 2 days\n\nComplicated cases or incomplete documentation may extend this timeline.",
      
      "When will I receive funds after approval?": "Funds are disbursed in 7-10 working days after your loan is approved and all conditions are met. For purchase loans, funds are released directly to the seller. For construction loans, disbursement occurs in stages based on construction progress. Each stage requires inspection verification. The first disbursement typically takes 7-10 days, while subsequent stage disbursements take 5-7 days after inspection approval.",
      
      "How long does document verification take?": "Document verification typically takes 5-7 working days. This includes verification of:\n\n• Personal identification documents: 1-2 days\n• Income and employment documents: 2-3 days\n• Property documents: 2-3 days\n\nThe process may take longer if additional clarifications or documents are required. You can check your document verification status through our customer portal or by contacting your assigned relationship manager.",
      
      "How often can I refinance?": "You can apply for refinancing after completing at least 2 years of regular payments on your existing loan. We allow refinancing once every 3 years under normal circumstances. Refinancing involves closing your existing loan and initiating a new one, potentially with a different rate or tenure. Processing time for refinancing is typically 15-30 days, and standard processing fees apply.",
      
      "How quickly can I get a statement for my loan?": "Loan statements can be accessed instantly through our online portal. For official printed statements, the processing time is:\n\n• Regular processing: 3-5 working days (free)\n• Express processing: 1-2 working days (service fee applies)\n\nStatements can be delivered electronically (email) or in physical form through courier. Annual statements are provided free of charge, while additional statement requests may incur a nominal fee.",
      
      "What are the turnaround times for inquiries?": "Our standard response times for different inquiry channels are:\n\n• Phone inquiries: Immediate to 30 minutes (depending on call volume)\n• Email inquiries: 24-48 hours\n• Written correspondences: 3-5 working days\n• Online portal requests: 24-48 hours\n• Branch visits: Same day resolution for most inquiries\n\nComplex inquiries may take longer to resolve. All inquiries are assigned a tracking number for follow-up.",
      
      "How do I navigate the portal?": "To navigate our customer portal:\n\n1. Log in at hbfc.com.pk using your credentials\n2. The dashboard shows your loan summary and recent activities\n3. Use the main menu to access different sections:\n   • Account Summary: View loan details\n   • Statements: Generate and download statements\n   • Payments: View history and make payments\n   • Documents: Upload and view documents\n   • Profile: Update personal information\n   • Messages: Communicate with your relationship manager\n\nTutorial videos are available in the Help section.",
      
      "How can I update my personal information?": "You can update your personal information through:\n\n1. Online Portal: Log in and go to Profile > Personal Information > Edit\n2. Branch Visit: Fill out the information update form at any branch\n3. Email: Send the update request with supporting documents to info@hbfc.com\n\nFor updates requiring verification (like address or identity documents), original documentation may be required. Processing time is typically 3-5 working days, after which you'll receive a confirmation notification.",
      
      "How do I check my loan balance?": "You can check your loan balance through:\n\n1. Online Portal: Log in to see your current balance on the dashboard\n2. Mobile App: View balance in the account summary section\n3. Customer Service: Call our helpline at 0800-42322\n4. SMS Service: Send 'BAL' to our short code 8253\n5. Branch Visit: Request balance information at any branch\n\nThe online and mobile options provide real-time balance information, including your outstanding principal, accrued markup, and next installment details.",
      
      "How do I download my payment history?": "To download your payment history:\n\n1. Log in to the customer portal at hbfc.com.pk\n2. Navigate to 'Statements' or 'Payment History'\n3. Select the date range for the history you need\n4. Choose your preferred format (PDF, Excel, or CSV)\n5. Click 'Generate Report' and then 'Download'\n\nPayment history is available for the past 3 years by default. For older records, please contact customer service. The downloaded report includes payment dates, amounts, and allocation between principal and markup.",
      
      "How do I submit documents online?": "To submit documents online:\n\n1. Log in to the customer portal\n2. Navigate to the 'Documents' or 'Document Upload' section\n3. Select the document type you're uploading\n4. Choose the file from your device (supported formats: PDF, JPG, PNG)\n5. Add any necessary descriptions or references\n6. Click 'Upload' and wait for confirmation\n\nMaximum file size is 5MB per document. For large files, you can split them into multiple uploads. You'll receive an email confirmation once your documents are received and another when they're processed.",
      
      "How do I set up automatic payments?": "You can set up automatic payments through:\n\n1. Standing Instructions: Visit your bank and set up a standing order\n2. Direct Debit: Fill out our direct debit mandate form\n3. Online Banking: Set up a recurring payment through your bank's online platform\n\nTo set up direct debit:\n1. Download the mandate form from our website\n2. Fill it out with your bank account details\n3. Submit the form to any HBFC branch\n4. Receive confirmation within 7-10 working days\n\nAutomatic payments help avoid late fees and keep your payment history consistent."
    };
    
    // Return the answer for the given question
    return faqDatabase[question] || null;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <ChatbotContainer>
      <ChatbotButton onClick={toggleChat}>
        <i className="fa fa-comments" aria-hidden="true">💬</i>
      </ChatbotButton>
      
      <ChatWindow isOpen={isOpen}>
        <ChatHeader>
          <ChatTitle>HBFC Assistant</ChatTitle>
          <CloseButton onClick={toggleChat}>✕</CloseButton>
        </ChatHeader>
        
        <ChatMessages>
          {messages.map((message, index) => (
            <div key={index}>
              <Message isBot={message.isBot}>
                {message.text}
              </Message>
              {message.isBot && message.options && message.options.length > 0 && (
                <OptionButtons>
                  {message.options.map((option, optionIndex) => (
                    <OptionButton 
                      key={optionIndex} 
                      onClick={() => handleOptionClick(option)}
                    >
                      {option}
                    </OptionButton>
                  ))}
                </OptionButtons>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </ChatMessages>
        
        <ChatInput>
          <InputField
            type="text"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <SendButton onClick={handleSendMessage}>➤</SendButton>
        </ChatInput>
      </ChatWindow>
    </ChatbotContainer>
  );
}

export default ChatBot; 